import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  CreditCard,
  CreditCardPlus,
  History,
  Loader2,
  RefreshCw,
  Search,
  UserCheck,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppData } from "@/context/AppDataContext";
import type {
  CreditAccount,
  CreditAgency,
  CreditCheckAddress,
  CreditCheckDetail,
  CustomerInfo,
  CurrentAddressStatus,
  CurrentEmploymentStatus,
  DrivingLicenceType,
  AddressMatchDetail,
} from "@/models/creditCheck";
import { CreditCheckVisibility } from "@/models/creditCheck";
import {
  creditCheckService,
  formatName,
  formatAddress,
  formatUnderwriteAddress,
  prettifyString,
  toTitleCase,
  getStatusClass,
  getStatusDescription,
  dateDiffDescription,
  getYears,
  getMonths,
  populateAddressPaymentSlots,
  is247MoneyAccount,
} from "@/services/CreditCheckService";
import { CreditCheckAccountDialog } from "./CreditCheckAccountDialog";

interface CreditCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: number | null;
}

const MAIN_CUSTOMER = "MainCustomer";

function formatCurrency(val?: number | null): string {
  if (val == null || Number.isNaN(val)) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(val);
}

function formatDate(val?: string | null): string {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(val?: string | null): string {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function CreditCheckDialog({
  open,
  onOpenChange,
  applicationId,
}: CreditCheckDialogProps) {
  const { currentUser } = useAppData();

  const [agency, setAgency] = useState<CreditAgency>("Equifax");
  const [isArchive, setIsArchive] = useState<boolean>(false);
  // Archive mode is hidden for now
  const showArchive = false;
  const [loading, setLoading] = useState<boolean>(false);
  const [runningCheck, setRunningCheck] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [creditCheck, setCreditCheck] = useState<CreditCheckDetail | null>(null);
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [residentialAddress, setResidentialAddress] = useState<CurrentAddressStatus | null>(null);
  const [employmentAddress, setEmploymentAddress] = useState<CurrentEmploymentStatus | null>(null);
  const [, setDrivingLicenceTypes] = useState<DrivingLicenceType[]>([]);

  const [showAssociates, setShowAssociates] = useState<boolean>(true);
  const [selectedAccount, setSelectedAccount] = useState<CreditAccount | null>(null);

  // Address match flow (for 422 response during runCreditCheck)
  const [addressMatch, setAddressMatch] = useState<AddressMatchDetail[] | null>(null);
  const [selectedMatchKeys, setSelectedMatchKeys] = useState<Record<number, string>>({});

  const levelOfDetail = useMemo(() => {
    if (currentUser?.creditCheckVisibilityId != null && currentUser.creditCheckVisibilityId > 0) {
      return currentUser.creditCheckVisibilityId;
    }
    return CreditCheckVisibility.FullView;
  }, [currentUser]);

  const years = useMemo(() => getYears(), []);
  const months = useMemo(() => getMonths(), []);

  // Fetch customer details
  const fetchCustomerDetails = useCallback(async (appId: number) => {
    try {
      const cust = await creditCheckService.getCustomerByApplicationId(appId);
      if (!cust) return;

      const [resAddr, empAddr, licenceTypes] = await Promise.all([
        cust.customerId
          ? creditCheckService.getCurrentResidentialAddress(cust.customerId).catch(() => null)
          : null,
        cust.customerId
          ? creditCheckService.getCurrentEmploymentAddress(cust.customerId).catch(() => null)
          : null,
        creditCheckService.getDrivingLicenceTypes().catch(() => []),
      ]);

      const licenceType = licenceTypes.find(
        (l) => l.drivingLicenceTypeId === cust.drvLicenceTypeId
      );

      setCustomer({
        ...cust,
        drivingLicenceTypeName: licenceType?.drivingLicenceTypeName ?? cust.drivingLicenceTypeName,
      });
      setResidentialAddress(resAddr);
      setEmploymentAddress(empAddr);
      setDrivingLicenceTypes(licenceTypes);
    } catch {
      // Customer detail fetch failure is non-fatal for credit check display
    }
  }, []);

  // Main search / load function
  const loadCreditCheck = useCallback(
    async (targetAgency: CreditAgency, targetArchive: boolean) => {
      if (applicationId == null || !Number.isFinite(applicationId)) return;

      setLoading(true);
      setErrorMessage(null);
      setAddressMatch(null);

      try {
        if (targetArchive) {
          const archiveData = await creditCheckService.getCreditCheckArchive(
            applicationId,
            targetAgency
          );
          if (archiveData && archiveData.addresses) {
            populateAddressPaymentSlots(archiveData.addresses, months);
          }
          setCreditCheck(archiveData);
        } else {
          const searchRes = await creditCheckService.searchCreditCheck(
            applicationId,
            new Date(1900, 1, 1)
          );

          if (!searchRes?.id) {
            setCreditCheck(null);
            setLoading(false);
            return;
          }

          const version = targetAgency === "Equifax" ? 2 : 1;
          const [checkData, summaryData] = await Promise.all([
            creditCheckService.getCreditCheck(searchRes.id, targetAgency, version),
            creditCheckService
              .getCreditCheckSummary(searchRes.id, targetAgency, version)
              .catch(() => undefined),
          ]);

          if (checkData) {
            if (summaryData) {
              checkData.summary = summaryData;
            }
            if (checkData.addresses) {
              populateAddressPaymentSlots(checkData.addresses, months);
            }
          }
          setCreditCheck(checkData);
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : "Could not find a credit check for this application and credit reference agency";
        setErrorMessage(message);
        setCreditCheck(null);
      } finally {
        setLoading(false);
      }
    },
    [applicationId, months]
  );

  // Initial load when dialog opens
  useEffect(() => {
    if (!open) {
      setCreditCheck(null);
      setErrorMessage(null);
      setAddressMatch(null);
      return;
    }

    if (applicationId != null && Number.isFinite(applicationId)) {
      fetchCustomerDetails(applicationId);
      loadCreditCheck(agency, isArchive);
    }
  }, [open, applicationId, agency, isArchive, fetchCustomerDetails, loadCreditCheck]);

  // Agency or Archive toggle handlers
  const handleSelectAgency = (newAgency: CreditAgency, archive: boolean) => {
    setAgency(newAgency);
    setIsArchive(archive);
    loadCreditCheck(newAgency, archive);
  };

  // Run credit check flow
  const handleRunCreditCheck = async (matchedAddresses?: Array<{
    key?: string;
    houseName?: string;
    houseNumber?: string;
    street?: string;
    subBuilding?: string;
    postcode: string;
    town?: string;
  }>) => {
    if (!applicationId || !customer) return;
    setRunningCheck(true);
    setErrorMessage(null);

    const version = agency === "Equifax" ? 2 : 1;
    let addresses = matchedAddresses;

    if (!addresses) {
      setAddressMatch(null);
      addresses = (customer.residentialAddresses ?? []).map((addr) => ({
        key: addr.key || "",
        houseName: addr.houseName || addr.buildingName || "",
        houseNumber: addr.houseNumber || addr.buildingNumber || "",
        street: addr.street || addr.streetName || "",
        subBuilding: addr.subBuilding || "",
        postcode: addr.postcode,
        town: addr.town || "",
      }));
    }

    const payload = {
      firstName: customer.firstName,
      lastName: customer.surname,
      dateOfBirth: customer.dateOfBirth,
      addresses,
      searchPurpose: "soft",
    };

    try {
      let existingId: string | null = null;
      try {
        const searchRes = await creditCheckService.searchCreditCheck(applicationId);
        existingId = searchRes?.id ?? null;
      } catch {
        existingId = null;
      }

      if (existingId) {
        await creditCheckService.reRunCreditCheck(existingId, agency, version, payload);
      } else {
        const newRes = await creditCheckService.runCreditCheck(agency, version, payload);
        if (newRes?.id) {
          await creditCheckService.updateFromPatch(newRes.id, applicationId);
        }
      }

      setAddressMatch(null);
      loadCreditCheck(agency, false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { details?: AddressMatchDetail[] } } };
      if (axiosErr?.response?.status === 422 && axiosErr.response.data?.details) {
        setAddressMatch(axiosErr.response.data.details);
      } else {
        setErrorMessage("Credit Check failed. Please check details and retry.");
      }
    } finally {
      setRunningCheck(false);
    }
  };

  const handleConfirmAddressMatches = () => {
    if (!customer?.residentialAddresses || !addressMatch) return;

    const matchedAddresses = customer.residentialAddresses.map((resAddr, idx) => {
      const selectedKey = selectedMatchKeys[idx] || resAddr.key || "";
      return {
        key: selectedKey,
        houseName: resAddr.houseName || resAddr.buildingName || "",
        houseNumber: resAddr.houseNumber || resAddr.buildingNumber || "",
        street: resAddr.street || resAddr.streetName || "",
        subBuilding: resAddr.subBuilding || "",
        postcode: resAddr.postcode,
        town: resAddr.town || "",
      };
    });

    handleRunCreditCheck(matchedAddresses);
  };

  // Filter accounts/sections by "show associates"
  const getFilteredAccounts = (addr: CreditCheckAddress): CreditAccount[] => {
    const list = addr.accounts ?? [];
    if (showAssociates) return list;
    return list.filter((a) => a.person?.accountHolderType === MAIN_CUSTOMER);
  };

  const getFilteredElectoralRoll = (addr: CreditCheckAddress) => {
    const list = addr.electoralRoll ?? [];
    if (showAssociates) return list;
    return list.filter((e) => e.person?.accountHolderType === MAIN_CUSTOMER);
  };

  const getFilteredCcj = (addr: CreditCheckAddress) => {
    const list = addr.ccj ?? [];
    if (showAssociates) return list;
    return list.filter((c) => c.person?.accountHolderType === MAIN_CUSTOMER);
  };

  const getFilteredNoticeOfCorrection = (addr: CreditCheckAddress) => {
    const list = addr.noticeOfCorrection ?? [];
    if (showAssociates) return list;
    return list.filter((n) => n.person?.accountHolderType === MAIN_CUSTOMER);
  };

  const getFilteredCifas = (addr: CreditCheckAddress) => {
    const list = addr.cifas ?? [];
    if (showAssociates) return list;
    return list.filter((c) => c.person?.accountHolderType === MAIN_CUSTOMER);
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 650) return "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300";
    if (score >= 500) return "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300";
    return "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300";
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex flex-col gap-0 overflow-hidden p-0 max-w-[95vw] lg:max-w-7xl max-h-[92vh] h-[92vh]"
        >
          {/* Header */}
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4 flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CreditCard className="size-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold tracking-tight">Credit Report</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Application {applicationId ?? "—"}
                </DialogDescription>
              </div>
            </div>

            {/* Top Toolbar Actions */}
            <div className="flex items-center gap-2 flex-wrap">
                {/* Refresh Button */}
                <Button
                    size="sm"
                    variant="ghost"
                    className="size-8 p-0"
                    disabled={loading || runningCheck}
                    onClick={() => loadCreditCheck(agency, isArchive)}
                    title="Refresh Report"
                >
                    <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
              {/* Agency Buttons */}
              <div className="flex rounded-md border border-border bg-muted/30 p-0.5 text-xs">
                
                <Button
                  size="sm"
                  variant={agency === "Equifax" && !isArchive ? "secondary" : "ghost"}
                  className="h-7 px-3 text-xs font-medium"
                  onClick={() => handleSelectAgency("Equifax", false)}
                >
                  Equifax
                </Button>
                <Button
                  size="sm"
                  variant={agency === "Callcredit" && !isArchive ? "secondary" : "ghost"}
                  className="h-7 px-3 text-xs font-medium"
                  onClick={() => handleSelectAgency("Callcredit", false)}
                >
                  TransUnion
                </Button>
                {showArchive && (
                  <>
                    <Button
                      size="sm"
                      variant={agency === "Equifax" && isArchive ? "secondary" : "ghost"}
                      className="h-7 px-3 text-xs font-medium gap-1"
                      onClick={() => handleSelectAgency("Equifax", true)}
                    >
                      <History className="size-3" />
                      Equifax Archive
                    </Button>
                    <Button
                      size="sm"
                      variant={agency === "Callcredit" && isArchive ? "secondary" : "ghost"}
                      className="h-7 px-3 text-xs font-medium gap-1"
                      onClick={() => handleSelectAgency("Callcredit", true)}
                    >
                      <History className="size-3" />
                      TransUnion Archive
                    </Button>
                  </>
                )}
              </div>

              {/* Run Credit Check Button */}
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 text-xs mr-4"
                disabled={loading || runningCheck || applicationId == null}
                onClick={() => handleRunCreditCheck()}
              >
                {runningCheck ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <CreditCardPlus className="size-3.5" />
                )}
                Run credit check
              </Button>
            </div>
          </DialogHeader>

          {/* Body Content */}
          <div className="relative min-h-0 flex-1 overflow-y-auto bg-muted/10 p-6 space-y-6">
            {/* Missing Application ID error */}
            {applicationId == null ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <AlertTriangle className="size-10 text-rose-500 mb-3" />
                <h3 className="text-base font-semibold">Application Not Found</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  The application ID could not be found. Please refresh and try again.
                </p>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-24 space-y-3" role="status">
                <Loader2 className="size-10 animate-spin text-primary" />
                <p className="text-sm font-medium text-muted-foreground">Loading Credit Report…</p>
                <span className="sr-only">Loading Credit Report</span>
              </div>
            ) : levelOfDetail <= CreditCheckVisibility.UnableToView ? (
              <div className="rounded-lg border border-border bg-card p-8 text-center">
                <p className="text-sm text-muted-foreground font-medium">
                  You do not have permission to view this data.
                </p>
              </div>
            ) : addressMatch && addressMatch.length > 0 ? (
              /* Address matching UI (422) */
              <div className="rounded-xl border border-border bg-card p-6 shadow-xs space-y-4">
                <div className="border-b border-border pb-3">
                  <h3 className="text-base font-semibold">Confirm Address Match</h3>
                  <p className="text-xs text-muted-foreground">
                    Multiple matching addresses were found. Please confirm the best match for the customer.
                  </p>
                </div>

                {addressMatch.map((detail, groupIdx) => {
                  const currentResAddr = customer?.residentialAddresses?.[groupIdx];
                  return (
                    <div key={groupIdx} className="rounded-lg border border-border/70 p-4 space-y-3">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">We have the following address for the customer:</p>
                        <p className="text-sm font-semibold text-foreground mt-0.5">
                          {formatUnderwriteAddress(currentResAddr)}
                        </p>
                      </div>

                      {detail.matchType === 1 ? (
                        <p className="text-xs text-rose-600 font-medium">
                          There is no matching address found, please confirm with the customer that this is correct.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">Select the best match from the list below:</p>
                          <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
                            {detail.keys.map((addr) => {
                              const isChecked = selectedMatchKeys[groupIdx] === addr.key;
                              return (
                                <label
                                  key={addr.key}
                                  className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-muted/50 cursor-pointer transition-colors"
                                >
                                  <Checkbox
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      setSelectedMatchKeys((prev) => ({
                                        ...prev,
                                        [groupIdx]: checked ? addr.key : "",
                                      }));
                                    }}
                                  />
                                  <span className="font-medium text-foreground">{addr.addressLine}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setAddressMatch(null)}>
                    Cancel
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConfirmAddressMatches}>
                    Confirm Matches
                  </Button>
                </div>
              </div>
            ) : !creditCheck ? (
              /* No credit check found */
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-4">
                <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Search className="size-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold">No Credit Check Found</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    {errorMessage ||
                      `Could not find an active ${agency} credit check for this application.`}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleRunCreditCheck()}
                  disabled={runningCheck}
                  className="gap-2"
                >
                  {runningCheck && <Loader2 className="size-3.5 animate-spin" />}
                  Run Credit Check Now
                </Button>
              </div>
            ) : (
              /* Main Credit Check Content */
              <>
                {/* Agency & Score Banner */}
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-xs">
                  <div className="flex items-center gap-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-foreground">
                          {creditCheck.agency}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-sm font-semibold px-2 py-0.5 border ${getScoreBadgeVariant(creditCheck.score)}`}
                        >
                          Score: {creditCheck.score}
                        </Badge>
                        <Badge variant="secondary" className="capitalize text-xs">
                          {creditCheck.searchPurpose || "Soft Search"}
                        </Badge>
                        {isArchive && (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Search Date: {formatDateTime(creditCheck.agencySearchDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Top 2 Columns: Customer & Summary */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Customer Card */}
                  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
                    <div className="border-b border-border bg-muted/30 px-4 py-3">
                      <h3 className="text-sm font-semibold flex items-center gap-2 text-foreground">
                        <UserCheck className="size-4 text-primary" />
                        Customer Details
                      </h3>
                    </div>
                    <div className="divide-y divide-border text-xs">
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Name</span>
                        <span className="font-semibold text-foreground text-right">
                          {formatName(customer) || formatName(creditCheck.person) || "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">DOB</span>
                        <span className="font-semibold text-foreground text-right">
                          {formatDate(customer?.dateOfBirth || creditCheck.person?.dateOfBirth)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Residential Status</span>
                        <span className="font-semibold text-foreground text-right">
                          {residentialAddress?.statusDescription || "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Employment Status</span>
                        <span className="font-semibold text-foreground text-right">
                          {employmentAddress?.statusDescription || "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Occupation</span>
                        <span className="font-semibold text-foreground text-right">
                          {employmentAddress?.occupation || "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Net Monthly Income</span>
                        <span className="font-semibold text-foreground text-right">
                          {formatCurrency(customer?.netMonthlyIncome)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Driving Licence</span>
                        <span className="font-semibold text-foreground text-right">
                          {customer?.drivingLicenceTypeName || "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Card */}
                  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
                    <div className="border-b border-border bg-muted/30 px-4 py-3">
                      <h3 className="text-sm font-semibold flex items-center gap-2 text-foreground">
                        <CreditCard className="size-4 text-primary" />
                        Credit Summary
                      </h3>
                    </div>
                    <div className="divide-y divide-border text-xs">
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Search Date</span>
                        <span className="font-semibold text-foreground text-right">
                          {formatDateTime(creditCheck.agencySearchDate)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Active Accounts</span>
                        <span className="font-semibold text-foreground text-right">
                          {creditCheck.summary?.activeAccounts ?? 0} (
                          {formatCurrency(creditCheck.summary?.activeAccountsTotalValue)})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Worst Status (12m)</span>
                        <span className="font-semibold text-foreground text-right">
                          {creditCheck.summary?.worstIn12Months ?? "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Worst Status (36m)</span>
                        <span className="font-semibold text-foreground text-right">
                          {creditCheck.summary?.worstIn36Months ?? "—"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Defaults</span>
                        <span className={`font-semibold text-right ${(creditCheck.summary?.defaults ?? 0) > 0 ? "text-rose-600" : "text-foreground"}`}>
                          {creditCheck.summary?.defaults ?? 0}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">CCJs</span>
                        <span className={`font-semibold text-right ${(creditCheck.summary?.cCJs ?? 0) > 0 ? "text-rose-600" : "text-foreground"}`}>
                          {creditCheck.summary?.cCJs ?? 0} ({formatCurrency(creditCheck.summary?.cCJTotalValue)})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Searches (Last 6 Months)</span>
                        <span className="font-semibold text-foreground text-right">
                          {creditCheck.summary?.searchesIn6Months ?? 0}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Pay Day Loans</span>
                        <span className="font-semibold text-foreground text-right">
                          {creditCheck.summary?.activePaydayLoans ?? 0} (
                          {formatCurrency(creditCheck.summary?.activePaydayLoansTotalValue)})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Total Revolving Limit</span>
                        <span className="font-semibold text-foreground text-right">
                          {formatCurrency(creditCheck.summary?.revolvingCreditLimit)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 px-4 py-2.5">
                        <span className="font-medium text-muted-foreground">Revolving Utilisation</span>
                        <span className="font-semibold text-foreground text-right">
                          {formatCurrency(creditCheck.summary?.revolvingCreditUtilisation)}{" "}
                          {creditCheck.summary?.revolvingCreditLimit
                            ? `(${((creditCheck.summary.revolvingCreditUtilisation / creditCheck.summary.revolvingCreditLimit) * 100).toFixed(1)}%)`
                            : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Addresses Accordion */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-foreground">Addresses & History</h3>
                    <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
                      <Checkbox
                        checked={showAssociates}
                        onCheckedChange={(c) => setShowAssociates(Boolean(c))}
                      />
                      <span>Show associates</span>
                    </label>
                  </div>

                  {creditCheck.addresses && creditCheck.addresses.length > 0 ? (
                    <Accordion
                      type="multiple"
                      defaultValue={[creditCheck.addresses[0]?.addressType ?? "item-0"]}
                      className="space-y-4"
                    >
                      {creditCheck.addresses.map((address, addrIdx) => {
                        const filteredAccounts = getFilteredAccounts(address);
                        const filteredElectoralRoll = getFilteredElectoralRoll(address);
                        const filteredPreviousSearches = address.previousSearches ?? [];
                        const filteredCcj = getFilteredCcj(address);
                        const filteredNotice = getFilteredNoticeOfCorrection(address);
                        const filteredCifas = getFilteredCifas(address);

                        return (
                          <AccordionItem
                            key={addrIdx}
                            value={address.addressType || `item-${addrIdx}`}
                            className="rounded-xl border border-border bg-card shadow-xs overflow-hidden"
                          >
                            <AccordionTrigger className="px-5 py-3 hover:no-underline bg-muted/20 hover:bg-muted/40 transition-colors">
                              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <Badge variant="outline" className="text-xs uppercase">
                                  {address.addressType || "Address"}
                                </Badge>
                                <span>{formatAddress(address)}</span>
                              </div>
                            </AccordionTrigger>

                            <AccordionContent className="p-5 space-y-6">
                              {/* 1. Credit Accounts Section */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                    Credit Accounts ({filteredAccounts.length})
                                  </h4>
                                  <p className="text-[11px] text-muted-foreground">
                                    Click any row to view full account details
                                  </p>
                                </div>

                                {filteredAccounts.length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic py-2">
                                    No accounts recorded for this address.
                                  </p>
                                ) : (
                                  <div className="relative overflow-x-auto rounded-lg border border-border">
                                    <table className="w-full text-xs border-collapse">
                                      <thead>
                                        {/* Year Header Row */}
                                        <tr className="border-b border-border bg-muted/60 text-muted-foreground">
                                          <th className="sticky left-0 z-20 bg-muted/90 px-3 py-2 text-left font-semibold min-w-[220px] border-r border-border backdrop-blur-xs">
                                            Account
                                          </th>
                                          {years.map((yearObj) => (
                                            <th
                                              key={yearObj.year}
                                              colSpan={yearObj.monthCount}
                                              className="border-r border-border text-center font-bold px-1 py-1.5"
                                            >
                                              {yearObj.year}
                                            </th>
                                          ))}
                                        </tr>
                                        {/* Month Header Row */}
                                        <tr className="border-b border-border bg-muted/30 text-muted-foreground text-[10px]">
                                          <th className="sticky left-0 z-20 bg-muted/80 px-3 py-1 text-left font-normal border-r border-border backdrop-blur-xs">
                                            Month
                                          </th>
                                          {months.map((m, mIdx) => (
                                            <th
                                              key={mIdx}
                                              className="text-center font-medium px-1 py-1 min-w-[24px] border-r border-border/50"
                                            >
                                              {m.month}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border">
                                        {filteredAccounts.map((account, accIdx) => {
                                          const is247 = is247MoneyAccount(
                                            creditCheck.agency,
                                            account.companyName
                                          );
                                          const slots = account.monthSlots ?? [];

                                          return (
                                            <tr
                                              key={accIdx}
                                              onClick={() => setSelectedAccount(account)}
                                              className="hover:bg-primary/5 cursor-pointer transition-colors"
                                            >
                                              <td className="sticky left-0 z-10 bg-card px-3 py-2 font-medium border-r border-border whitespace-nowrap shadow-xs">
                                                <div className="flex items-center gap-1.5">
                                                  <span className="font-semibold text-foreground">
                                                    {formatName(account.person)}
                                                  </span>
                                                  <span className="text-muted-foreground">
                                                    ({prettifyString(account.accountType)})
                                                  </span>
                                                  {is247 && (
                                                    <TooltipProvider>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <span className="inline-flex items-center text-amber-500">
                                                            <AlertTriangle className="size-3.5" />
                                                          </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent className="text-xs">
                                                          Loan With 247 Money
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                </div>
                                              </td>

                                              {/* Month status cells */}
                                              {slots.map((slot, sIdx) => {
                                                const sClass = getStatusClass(slot.status);
                                                return (
                                                  <td
                                                    key={sIdx}
                                                    title={
                                                      slot.status
                                                        ? `${getStatusDescription(slot.status)} (${formatDate(slot.date?.toISOString())})`
                                                        : ""
                                                    }
                                                    className={`text-center font-bold px-1 py-1 text-[11px] border-r border-border/40 ${sClass}`}
                                                  >
                                                    {slot.status || ""}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {/* Status Legend */}
                                <div className="flex flex-wrap items-center gap-3 pt-2 text-[11px] text-muted-foreground">
                                  <span className="font-semibold">Legend:</span>
                                  <div className="flex items-center gap-1">
                                    <span className="size-3 rounded-xs border border-emerald-300 bg-[#caffc6]" />
                                    <span>0 = Up to date</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="size-3 rounded-xs border border-amber-300 bg-[#f4c180]" />
                                    <span>1-5 = Arrears</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="size-3 rounded-xs border border-rose-400 bg-[#ff7058]" />
                                    <span>6, D, R = Default / Repo</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="size-3 rounded-xs border border-amber-200 bg-[#fffecb]" />
                                    <span>S = Settled</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="size-3 rounded-xs border border-zinc-300 bg-[#e8e8ea]" />
                                    <span>I = Arrangement</span>
                                  </div>
                                </div>
                              </div>

                              {/* 2. Voters Roll Section */}
                              <div className="space-y-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                  Voters Roll ({filteredElectoralRoll.length})
                                </h4>
                                {filteredElectoralRoll.length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic py-1">
                                    No voter registration records found.
                                  </p>
                                ) : (
                                  <div className="overflow-hidden rounded-lg border border-border">
                                    <table className="w-full text-xs">
                                      <thead className="bg-muted/40 border-b border-border text-left">
                                        <tr>
                                          <th className="px-3 py-2 font-semibold">Name</th>
                                          <th className="px-3 py-2 font-semibold">Current</th>
                                          <th className="px-3 py-2 font-semibold">From</th>
                                          <th className="px-3 py-2 font-semibold">End</th>
                                          <th className="px-3 py-2 font-semibold">Duration</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border">
                                        {filteredElectoralRoll.map((er, idx) => (
                                          <tr key={idx} className="hover:bg-muted/20">
                                            <td className="px-3 py-2 font-medium">
                                              {formatName(er.person)}
                                            </td>
                                            <td className="px-3 py-2">
                                              {er.endDate == null ? (
                                                <span className="inline-flex items-center text-emerald-600 font-semibold gap-1">
                                                  <Check className="size-3.5" /> Yes
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center text-rose-500 font-semibold gap-1">
                                                  <X className="size-3.5" /> No
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2">{formatDate(er.startDate)}</td>
                                            <td className="px-3 py-2">{formatDate(er.endDate)}</td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                              {dateDiffDescription(er.startDate, er.endDate)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {/* 3. Previous Credit Searches Section */}
                              <div className="space-y-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                  Previous Credit Searches ({filteredPreviousSearches.length})
                                </h4>
                                {filteredPreviousSearches.length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic py-1">
                                    No previous searches recorded.
                                  </p>
                                ) : (
                                  <div className="overflow-hidden rounded-lg border border-border">
                                    <table className="w-full text-xs">
                                      <thead className="bg-muted/40 border-b border-border text-left">
                                        <tr>
                                          <th className="px-3 py-2 font-semibold">Name</th>
                                          <th className="px-3 py-2 font-semibold">Search Date</th>
                                          <th className="px-3 py-2 font-semibold">Duration</th>
                                          <th className="px-3 py-2 font-semibold">Company Type</th>
                                          <th className="px-3 py-2 font-semibold">Search Type</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border">
                                        {filteredPreviousSearches.map((srch, idx) => (
                                          <tr key={idx} className="hover:bg-muted/20">
                                            <td className="px-3 py-2 font-medium">
                                              {formatName(srch.person)}
                                            </td>
                                            <td className="px-3 py-2">{formatDate(srch.searchDate)}</td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                              {dateDiffDescription(srch.searchDate, null)}
                                            </td>
                                            <td className="px-3 py-2">{prettifyString(srch.companyType)}</td>
                                            <td className="px-3 py-2">{prettifyString(srch.searchType)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {/* 4. Court & Public Information (CCJs) */}
                              <div className="space-y-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                  Court & Public Information (CCJs) ({filteredCcj.length})
                                </h4>
                                {filteredCcj.length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic py-1">
                                    No court or public records found.
                                  </p>
                                ) : (
                                  <div className="overflow-hidden rounded-lg border border-border">
                                    <table className="w-full text-xs">
                                      <thead className="bg-muted/40 border-b border-border text-left">
                                        <tr>
                                          <th className="px-3 py-2 font-semibold">Name</th>
                                          <th className="px-3 py-2 font-semibold">Is Satisfied?</th>
                                          <th className="px-3 py-2 font-semibold">Value</th>
                                          <th className="px-3 py-2 font-semibold">Court Date</th>
                                          <th className="px-3 py-2 font-semibold">Type</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border">
                                        {filteredCcj.map((ccj, idx) => (
                                          <tr key={idx} className="hover:bg-muted/20">
                                            <td className="px-3 py-2 font-medium">
                                              {formatName(ccj.person)}
                                            </td>
                                            <td className="px-3 py-2">
                                              {ccj.satisfiedDate != null ? (
                                                <span className="inline-flex items-center text-emerald-600 font-semibold gap-1">
                                                  <Check className="size-3.5" /> Satisfied
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center text-rose-500 font-semibold gap-1">
                                                  <X className="size-3.5" /> Unsatisfied
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 font-medium">{formatCurrency(ccj.value)}</td>
                                            <td className="px-3 py-2">{formatDate(ccj.courtDate)}</td>
                                            <td className="px-3 py-2">{toTitleCase(ccj.ccjType)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {/* 5. Notices of Correction */}
                              <div className="space-y-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                  Notices of Correction ({filteredNotice.length})
                                </h4>
                                {filteredNotice.length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic py-1">
                                    No notices of correction.
                                  </p>
                                ) : (
                                  <div className="overflow-hidden rounded-lg border border-border">
                                    <table className="w-full text-xs">
                                      <thead className="bg-muted/40 border-b border-border text-left">
                                        <tr>
                                          <th className="px-3 py-2 font-semibold">Name</th>
                                          <th className="px-3 py-2 font-semibold">Created</th>
                                          <th className="px-3 py-2 font-semibold">Text</th>
                                          <th className="px-3 py-2 font-semibold">Type</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border">
                                        {filteredNotice.map((noc, idx) => (
                                          <tr key={idx} className="hover:bg-muted/20">
                                            <td className="px-3 py-2 font-medium">{formatName(noc.person)}</td>
                                            <td className="px-3 py-2">{formatDate(noc.createdDate)}</td>
                                            <td className="px-3 py-2 text-muted-foreground">{noc.text}</td>
                                            <td className="px-3 py-2">{noc.typeOfData}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {/* 6. CIFAS (Underwriting view) */}
                              {levelOfDetail > CreditCheckVisibility.PartialView && (
                                <div className="space-y-2">
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                    CIFAS ({filteredCifas.length})
                                  </h4>
                                  {filteredCifas.length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic py-1">
                                      No CIFAS records found.
                                    </p>
                                  ) : (
                                    <div className="overflow-hidden rounded-lg border border-border">
                                      <table className="w-full text-xs">
                                        <thead className="bg-muted/40 border-b border-border text-left">
                                          <tr>
                                            <th className="px-3 py-2 font-semibold">Name</th>
                                            <th className="px-3 py-2 font-semibold">Case Type</th>
                                            <th className="px-3 py-2 font-semibold">Application Date</th>
                                            <th className="px-3 py-2 font-semibold">Fraud Date</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                          {filteredCifas.map((cif, idx) => (
                                            <tr key={idx} className="hover:bg-muted/20">
                                              <td className="px-3 py-2 font-medium">
                                                {formatName(cif.person)}
                                              </td>
                                              <td className="px-3 py-2">{cif.caseType}</td>
                                              <td className="px-3 py-2">{formatDate(cif.applicationDate)}</td>
                                              <td className="px-3 py-2">{formatDate(cif.fraudDate)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No addresses associated with this credit check.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Details Popup Dialog */}
      <CreditCheckAccountDialog
        open={selectedAccount != null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedAccount(null);
        }}
        account={selectedAccount}
      />
    </>
  );
}
